import { decryptData, encryptData } from "./browserEncryption";
import { logger } from "../../Utils/logger";
import { storageKeyLabel } from "./storageKeyLabel";

/**
 * DHT-5787 — some browsers expose `window.localStorage` as `null` (storage
 * disabled) and others throw a `SecurityError` on the property access itself
 * (site data blocked). Persisted Recoil atoms read storage during hydration,
 * above every error boundary, so either one crashed the app at boot. Every
 * export below reaches storage through `localStore()`, which falls back to an
 * in-memory store for the page load: reads come back empty, and a sign-in
 * survives until the next reload.
 *
 * Only reaching storage is guarded. Operations on a reachable store still
 * throw, so callers' quota recovery (DHT-3557) keeps working.
 */
const memoryValues = new Map<string, string>();

const memoryStore: Storage = {
  get length() {
    return memoryValues.size;
  },
  key: (index: number) => Array.from(memoryValues.keys())[index] ?? null,
  getItem: (key: string) => memoryValues.get(key) ?? null,
  setItem: (key: string, value: string) => {
    memoryValues.set(key, String(value));
  },
  removeItem: (key: string) => {
    memoryValues.delete(key);
  },
  clear: () => {
    memoryValues.clear();
  },
};

let fallbackLogged = false;

const fallBackToMemory = (reason: string): Storage => {
  if (!fallbackLogged) {
    fallbackLogged = true;
    logger.warn("localStorage unavailable — using in-memory storage", {
      reason,
    });
  }
  return memoryStore;
};

// Resolved on every call, so the real store is used whenever it is reachable.
// Reaching it drops the fallback's values: the real store is the source of
// truth from then on, so a value cleared or deleted there cannot resurface
// from memory if storage becomes unreachable again before a reload.
const localStore = (): Storage => {
  try {
    const store = globalThis.window?.localStorage;
    if (!store) return fallBackToMemory("null");
    memoryValues.clear();
    return store;
  } catch (error) {
    // Read `name` structurally: a DOMException is not an `Error` instance in
    // every engine.
    const name = (error as { name?: unknown } | null)?.name;
    return fallBackToMemory(typeof name === "string" ? name : "unknown");
  }
};

/**
 * DHT-4542 — persisted reads run synchronously during Recoil atom hydration at
 * boot, above the app's error boundaries, so an unguarded `JSON.parse` on a
 * corrupt or shape-incompatible stored value unmounted the whole tree (blank
 * screen on launch). Drop the offending key on failure: the value is
 * unreadable, and keeping it would re-throw on every subsequent launch.
 */
const parseStoredValue = <T>(key: string, value: string): T | null => {
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    // Deliberately not logging the parse error's message: engines echo a
    // fragment of the offending input into it, and these values include auth
    // tokens. The key namespace, error name and length are enough to diagnose.
    logger.error("Corrupt persisted storage value — clearing key", {
      key: storageKeyLabel(key),
      errorName: error instanceof Error ? error.name : "unknown",
      valueLength: value.length,
    });

    try {
      localStore().removeItem(key);
    } catch (deleteError) {
      logger.error("Failed to clear corrupt storage key", {
        key: storageKeyLabel(key),
        errorName: deleteError instanceof Error ? deleteError.name : "unknown",
      });
    }

    return null;
  }
};

export const isQuotaExceededError = (error: unknown): boolean => {
  return (
    error instanceof DOMException &&
    (error.name === "QuotaExceededError" ||
      error.name === "NS_ERROR_DOM_QUOTA_REACHED")
  );
};

export const clearGraphQLCache = (): void => {
  const store = localStore();
  const keysToDelete: string[] = [];

  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key?.startsWith("cache:")) {
      keysToDelete.push(key);
    }
  }

  keysToDelete.forEach(key => store.removeItem(key));
};

export const clearOtherUserTokens = (keepUserKey?: string): void => {
  const store = localStore();
  const keysToDelete: string[] = [];

  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key?.startsWith("token:") && key !== keepUserKey) {
      keysToDelete.push(key);
    }
  }

  keysToDelete.forEach(key => store.removeItem(key));
};

export const clearAllStorage = (): void => {
  localStore().clear();
};

export const setStorage = <T>(key: string, object: T) => {
  localStore().setItem(key, JSON.stringify(object));
};

export const getStorage = <T>(key: string): T | null => {
  const object = localStore().getItem(key);

  if (!object) return null;

  return parseStoredValue<T>(key, object);
};

export const deleteStorageKey = (key: string) => {
  localStore().removeItem(key);
};

// Callers iterate the result with `for…in`. The real store is returned as
// before; the fallback returns a plain snapshot so only stored keys enumerate.
export const listStorageKeys = (): Storage | Record<string, string> => {
  const store = localStore();
  return store === memoryStore ? Object.fromEntries(memoryValues) : store;
};

export const setSecureStorage = async <T>(
  key: string,
  object: T,
  encryptionKey: string,
): Promise<void> => {
  const encryptedData = await encryptData(
    JSON.stringify(object),
    encryptionKey,
  );

  localStore().setItem(key, encryptedData);
};

export const getSecureStorage = async <T>(
  key: string,
  encryptionKey: string,
): Promise<T | null> => {
  const object = localStore().getItem(key);

  if (!object) return null;

  const decryptedData = await decryptData(object, encryptionKey);

  if (!decryptedData) return null;

  return parseStoredValue<T>(key, decryptedData);
};
